import { apiFetch } from "@/lib/api";
import { type AuthUser, useAuthStore } from "@/features/auth/store/auth-store";

/** The Django API wraps bodies as `{ success, message, data, meta }`. */
interface Envelope<T> {
  data: T;
}

/** Org role hierarchy (lowest→highest authority), matching the backend. */
export type MemberRole = "staff" | "team_leader" | "country_director";

/** Lifecycle state the server derives — an invited member (pending, can't sign
 * in yet) is distinct from a deactivated one. */
export type MemberStatus = "active" | "invited" | "deactivated";

// Ordered highest→lowest for the role dropdown; labels match the backend.
export const ROLE_OPTIONS: { value: MemberRole; label: string }[] = [
  { value: "country_director", label: "Country Director" },
  { value: "team_leader", label: "Team Leader" },
  { value: "staff", label: "Staff" },
];

/** Which of the firm's clients this member may see (admins always see all). */
export type ClientVisibility = "all" | "assigned";

export const VISIBILITY_OPTIONS: { value: ClientVisibility; label: string }[] = [
  { value: "all", label: "Show All Clients" },
  { value: "assigned", label: "Show only assigned clients" },
];

export interface Member {
  id: string;
  name: string;
  email: string;
  role: MemberRole | null;
  /** Platform identity — GA/SA outrank the org roles and are labelled by these. */
  isSuperuser: boolean;
  isPlatformAdmin: boolean;
  isActive: boolean;
  emailVerified: boolean;
  status: MemberStatus;
  /** The practitioner this member defaults to for e-Services, if any. */
  defaultPractitionerId: string | null;
  defaultPractitionerName: string | null;
  clientVisibility: ClientVisibility;
  /** True when this (Country Director) member is also a linked practitioner. */
  isPractitioner: boolean;
}

interface ApiMember {
  id: number | string;
  name: string;
  email: string;
  role: MemberRole | null;
  is_superuser?: boolean;
  is_platform_admin?: boolean;
  is_active: boolean;
  email_verified: boolean;
  status: MemberStatus;
  default_practitioner: string | null;
  default_practitioner_name: string | null;
  client_visibility: ClientVisibility;
  is_practitioner: boolean;
}

function toMember(m: ApiMember): Member {
  return {
    id: String(m.id),
    name: m.name,
    email: m.email,
    role: m.role,
    isSuperuser: m.is_superuser ?? false,
    isPlatformAdmin: m.is_platform_admin ?? false,
    isActive: m.is_active,
    emailVerified: m.email_verified,
    status: m.status,
    defaultPractitionerId: m.default_practitioner,
    defaultPractitionerName: m.default_practitioner_name,
    clientVisibility: m.client_visibility,
    isPractitioner: m.is_practitioner,
  };
}

/**
 * GET /firm/members — the firm's users. Pass `firm` to list another accessible
 * firm's members (a house user acting on behalf of a BP); omit for own firm.
 * Pass `includeHouse` alongside `firm` to also include the house (NSI) team
 * members — the server honours it only for a Country-Director-or-above house
 * caller (used by the Add-Client team picker for a BP's client).
 */
export async function listMembers(firm?: string, includeHouse = false): Promise<Member[]> {
  const query = firm
    ? `/firm/members?page_size=100&firm=${encodeURIComponent(firm)}${includeHouse ? "&include_house=1" : ""}`
    : "/firm/members?page_size=100";
  const body = await apiFetch<Envelope<ApiMember[]>>(query);
  return body.data.map(toMember);
}

/** POST /firm/members — add a user directly (admin sets the password); they're
 * active immediately, no invitation email. */
export async function createMember(payload: {
  name: string;
  email: string;
  password: string;
  role: MemberRole;
  clientVisibility: ClientVisibility;
  /** Only meaningful for a Country Director — also create a linked practitioner. */
  isPractitioner: boolean;
}): Promise<Member> {
  const body = await apiFetch<Envelope<ApiMember>>("/firm/members", {
    method: "POST",
    json: {
      name: payload.name,
      email: payload.email,
      password: payload.password,
      role: payload.role,
      client_visibility: payload.clientVisibility,
      is_practitioner: payload.isPractitioner,
    },
  });
  return toMember(body.data);
}

/** PATCH /firm/members/:id — sparse update of a member's role (last-admin
 * guarded), client visibility, active status, and/or an admin-set password. */
export async function updateMember(
  id: string,
  changes: {
    role?: MemberRole;
    clientVisibility?: ClientVisibility;
    isActive?: boolean;
    password?: string;
  },
): Promise<Member> {
  const json: Record<string, string | boolean> = {};
  if (changes.role !== undefined) json.role = changes.role;
  if (changes.clientVisibility !== undefined) {
    json.client_visibility = changes.clientVisibility;
  }
  if (changes.isActive !== undefined) json.is_active = changes.isActive;
  if (changes.password) json.password = changes.password;
  const body = await apiFetch<Envelope<ApiMember>>(`/firm/members/${id}`, {
    method: "PATCH",
    json,
  });
  return toMember(body.data);
}

/** DELETE /firm/members/:id — deactivate a member (self / last-admin guarded). */
export async function deactivateMember(id: string): Promise<void> {
  await apiFetch<void>(`/firm/members/${id}`, { method: "DELETE" });
}

/** POST /firm/members/:id/resend — re-email a fresh invite to a pending member. */
export async function resendInvite(id: string): Promise<Member> {
  const body = await apiFetch<Envelope<ApiMember>>(`/firm/members/${id}/resend`, {
    method: "POST",
  });
  return toMember(body.data);
}

interface AcceptResponse {
  access: string;
  refresh: string;
  user: AuthUser;
}

/**
 * POST /firm/invitations/accept (public) — set the invitee's name + password
 * from the emailed token, then store the returned session so they land signed
 * in.
 */
export async function acceptInvitation(
  token: string,
  password: string,
  name: string,
): Promise<AuthUser> {
  const { data } = await apiFetch<Envelope<AcceptResponse>>("/firm/invitations/accept", {
    method: "POST",
    auth: false,
    json: { token, password, name },
  });
  useAuthStore.getState().setSession({
    user: data.user,
    accessToken: data.access,
    refreshToken: data.refresh,
  });
  return data.user;
}
